/**
 * Exportação local de resultados já disponíveis no Viewer. As funções `construir*` são puras e
 * não tocam o DOM; `baixarArquivo` é a única que interage com o navegador. Nada é enviado a
 * servidores: o conteúdo nasce e termina na máquina do usuário.
 */
export const TIPO_CSV = 'text/csv;charset=utf-8';
export const TIPO_GEOJSON = 'application/geo+json;charset=utf-8';
const BOM_UTF8 = '﻿';
const DELIMITADOR_CSV = ';';
const QUEBRA_DE_LINHA_CSV = '\r\n';
/** Início de fórmula em planilhas, ignorando espaços e caracteres de controle iniciais. */
// eslint-disable-next-line no-control-regex
const INICIO_DE_FORMULA = /^[\s\u0000-\u001f]*[=+\-@]/;
/** Valor de atributo como texto de célula, sem a proteção contra fórmulas. */
function valorComoTexto(valor) {
    if (valor === null || valor === undefined) {
        return '';
    }
    if (typeof valor === 'string') {
        return valor;
    }
    if (typeof valor === 'object') {
        return JSON.stringify(valor);
    }
    return typeof valor === 'number' || typeof valor === 'boolean' || typeof valor === 'bigint'
        ? String(valor)
        : '';
}
/**
 * Neutraliza texto que uma planilha interpretaria como fórmula, prefixando um apóstrofo (o
 * tratamento recomendado pela OWASP). Aplica-se a textos; números reais não são alterados.
 */
function neutralizarFormula(texto) {
    return INICIO_DE_FORMULA.test(texto) ? `'${texto}` : texto;
}
function escaparCelula(texto) {
    return /[;"\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}
function celula(valor) {
    const texto = valorComoTexto(valor);
    return escaparCelula(typeof valor === 'string' ? neutralizarFormula(texto) : texto);
}
/**
 * CSV das feições recebidas: uma linha por feição, `id` na primeira coluna e, nas demais, a união
 * ordenada das chaves de `atributos`. UTF-8 com BOM, delimitador `;` e quebras de linha CRLF.
 */
export function construirCsv(registros) {
    const chaves = new Set();
    for (const registro of registros) {
        for (const chave of Object.keys(registro.atributos)) {
            chaves.add(chave);
        }
    }
    const colunas = [...chaves].sort();
    // A coluna `id` é o identificador público; um atributo de origem com esse nome é desambiguado.
    const cabecalho = ['id', ...colunas.map((nome) => (nome === 'id' ? 'atributos.id' : nome))].map((nome) => celula(nome));
    const linhas = registros.map((registro) => [
        celula(registro.id),
        ...colunas.map((coluna) => celula(Object.prototype.hasOwnProperty.call(registro.atributos, coluna)
            ? registro.atributos[coluna]
            : null)),
    ].join(DELIMITADOR_CSV));
    return BOM_UTF8 + [cabecalho.join(DELIMITADOR_CSV), ...linhas].join(QUEBRA_DE_LINHA_CSV) + QUEBRA_DE_LINHA_CSV;
}
/** `FeatureCollection` com exatamente uma `Feature`: geometria e atributos como recebidos da API. */
export function construirGeoJsonDeFeicao(feicao) {
    const feature = {
        type: 'Feature',
        id: feicao.id,
        geometry: feicao.geometria,
        properties: { ...feicao.atributos },
    };
    return { type: 'FeatureCollection', features: [feature] };
}
const MODELO_DA_MEDICAO = 'esferico_aproximado';
const CARATER_DA_MEDICAO = 'estimativa_cartografica_nao_oficial';
/** `FeatureCollection` com a única `Feature` da medição; valores internos, sem arredondamento. */
export function construirGeoJsonDeMedicao(medicao) {
    const vertices = medicao.vertices.map((vertice) => [vertice[0], vertice[1]]);
    const feature = medicao.modo === 'distancia'
        ? {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: vertices },
            properties: {
                tipo_medicao: 'distancia',
                modelo: MODELO_DA_MEDICAO,
                distancia_m: medicao.distanciaM,
                carater: CARATER_DA_MEDICAO,
            },
        }
        : {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                // O anel termina no primeiro vértice; a medição guarda os vértices sem repeti-lo.
                coordinates: [[...vertices, [...(vertices[0] ?? [])]]],
            },
            properties: {
                tipo_medicao: 'area',
                modelo: MODELO_DA_MEDICAO,
                area_m2: medicao.areaM2,
                perimetro_m: medicao.perimetroM,
                carater: CARATER_DA_MEDICAO,
            },
        };
    return { type: 'FeatureCollection', features: [feature] };
}
/** Texto seguro para nome de arquivo: minúsculas, sem acentos e só `a-z`, `0-9`, `.`, `_` e `-`. */
export function nomeSeguro(texto) {
    const limpo = texto
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^[-.]+|[-.]+$/g, '');
    return limpo === '' ? 'sem-nome' : limpo;
}
export const nomeDoCsvDeFeicoes = (camadaId, quantidade) => `feicoes-${nomeSeguro(camadaId)}-${quantidade}-carregadas.csv`;
export const nomeDoGeoJsonDeFeicao = (camadaId, feicaoId) => `feicao-${nomeSeguro(camadaId)}-${feicaoId}.geojson`;
export const nomeDoGeoJsonDeMedicao = (modo) => `medicao-${modo}.geojson`;
/**
 * Dispara o download de `conteudo` pelo navegador: `Blob`, URL temporária e `<a download>`. A URL
 * é revogada logo após o clique (adiada para o próximo ciclo, a fim de não interromper o download).
 */
export function baixarArquivo(conteudo, nomeDoArquivo, tipo) {
    const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }));
    const ancora = document.createElement('a');
    ancora.href = url;
    ancora.download = nomeDoArquivo;
    ancora.style.display = 'none';
    document.body.appendChild(ancora);
    try {
        ancora.click();
    }
    finally {
        ancora.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }
}
export const serializarGeoJson = (documento) => JSON.stringify(documento, null, 2);
